import { useMemo } from "react";
import { DetailTotalData } from "src/api/contract/contract-types";
import { BaseTooltip } from "src/components";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import { ArticleSection } from "src/components/layout/ArticleSection";
import BaseSection from "src/components/layout/BaseSection";
import ProductTypeChangeLabel from "src/components/ProductTypeChangeLabel";
import { PagePath } from "src/pages/product/details";
import { numberToStringWithComma } from "src/utils/common-util";

type Props = {
  detailData: DetailTotalData; //상품 상세 조회 정보
  isUseApplication: boolean; // 이용신청서 사용 여부
};

const ContractProductInfo = ({ detailData, isUseApplication }: Props) => {
  // 상태 관리

  const productDetail = useMemo(() => detailData.product, [detailData.product]);
  const providerDetail = useMemo(() => detailData.provider, [detailData.provider]);

  const formatCancelableTime = (minutes: number | undefined): string => {
    if (!minutes) return "0분 전까지";

    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const mins = minutes % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}일`);
    if (hours > 0) parts.push(`${hours}시`);
    if (mins > 0) parts.push(`${mins}분`);

    return parts.length > 0 ? `${parts.join(" ")} 전까지` : "0분 전까지";
  };

  const goProductDetail = (id: number) => {
    let path = "";
    if (id) {
      path = PagePath.product.detail.replace(":id", id!.toString());
    }
    return path;
  };

  const goProviderDetail = (id: number) => {
    let path = "";
    if (id) {
      path = PagePath.provider.detail.replace(":id", id!.toString());
    }
    return path;
  };

  return (
    <ArticleSection
      title={"상품 정보"}
      children={
        <>
          <div className="flex-center gap10">
            <BaseSection
              title={"상품명"}
              children={
                <div className="flex-row flex-center-start w-100 font14">
                  <div className={detailData?.product?.productName.length > 15 ? "" : "mr5"}>
                    {detailData?.product?.productName !== "" && detailData?.product?.productName.length > 15 ? (
                      <div className={"flex-center-start"}>
                        <p>{detailData?.product?.productName!.substring(0, 15) + "..."}</p>
                        <BaseTooltip contents={""} tooltip={detailData?.product?.productName} touchIcon={"QUESTION"} />
                      </div>
                    ) : (
                      detailData?.product?.productName
                    )}{" "}
                  </div>
                  <div className="flex-center no-wrap">
                    (상품 ID: {productDetail?.id && <BaseNewTabLink path={goProductDetail(productDetail?.id)} value={productDetail?.id || "-"} ellipsis />}
                    )
                  </div>
                </div>
              }
            />
            {detailData?.product?.productType !== "TIME_COURT" && (
              <BaseSection
                title={"보증금"}
                children={
                  <p className="mr10 font-weight-bold">{productDetail?.deposit ? numberToStringWithComma(productDetail?.deposit) : "-"} 원</p>
                }
              />
            )}
          </div>
          <div className="flex-center gap10">
            <BaseSection title={"상품유형"} children={<ProductTypeChangeLabel productType={productDetail?.productType} />} />
            {detailData?.product?.productType !== "TIME_COURT" && (
              <BaseSection
                title={"ㄴ 계약금"}
                children={<p>{productDetail?.earnest ? numberToStringWithComma(productDetail?.earnest) : "-"} 원</p>}
              />
            )}
          </div>
          <div className="flex-center gap10">
            <BaseSection
              title={"프로바이더 명"}
              children={
                <div className="flex-row flex-center-start w-100 font14">
                  <div
                    className={
                      providerDetail?.provider?.providerName !== "" &&
                      providerDetail?.provider?.providerName !== undefined &&
                      providerDetail?.provider?.providerName!.length > 15
                        ? ""
                        : "mr5"
                    }
                  >
                    {providerDetail?.provider?.providerName !== "" &&
                    providerDetail?.provider?.providerName !== undefined &&
                    providerDetail?.provider?.providerName!.length > 15 ? (
                      <div className={"flex-center-start"}>
                        <p>{providerDetail?.provider?.providerName!.substring(0, 15) + "..."}</p>
                        <BaseTooltip contents={""} tooltip={providerDetail?.provider?.providerName} touchIcon={"QUESTION"} />
                      </div>
                    ) : (
                      providerDetail?.provider?.providerName
                    )}{" "}
                  </div>
                  <span className="no-wrap">(정산정보 ID:</span>
                  {providerDetail?.provider?.providerId && (
                    <BaseNewTabLink
                      path={goProviderDetail(Number(providerDetail?.provider?.providerId))}
                      value={String(providerDetail?.provider?.providerId) || "-"}
                      ellipsis
                    />
                  )}
                  )
                </div>
              }
            />
            {detailData?.product?.productType !== "TIME_COURT" && (
              <BaseSection
                title={"ㄴ 잔금"}
                children={
                  <p>{productDetail?.deposit ? numberToStringWithComma(Number(productDetail?.deposit) - Number(productDetail?.earnest)) : "-"} 원</p>
                }
              />
            )}
          </div>

          {/* 승인유형 / 이용신청서 */}
          {productDetail?.productType === "TIME_COURT" && (
            <>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>승인 유형</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <span>{detailData.contract?.approvalType === "AUTO" ? "자동승인" : "관리자승인"}</span>
                  </div>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>이용신청서</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <span>{isUseApplication ? "사용" : "미사용"}</span>
                  </div>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>온라인 결제</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <span>{productDetail?.isOnlinePayment ? "사용" : "미사용"}</span>
                  </div>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p>이용자 취소 가능 여부</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <span>
                      {detailData.contract?.isMemberCancelable ? (
                        <>{`취소 가능 (이용시작일시 ${formatCancelableTime(detailData.contract?.memberCancelableMinutes)})`}</>
                      ) : (
                        "취소 불가"
                      )}
                    </span>
                  </div>
                </div>
              </section>
            </>
          )}

          {detailData?.product?.productType !== "TIME_COURT" && (
            <div className="flex-center">
              <BaseSection />
              <BaseSection
                title={"월 이용료(부가세 포함)"}
                children={
                  <p>
                    {productDetail?.productionPrice
                      ? numberToStringWithComma(Number(productDetail?.productionPrice) + Number(productDetail?.productionPrice / 10))
                      : "0"}{" "}
                    원
                  </p>
                }
              />
            </div>
          )}
        </>
      }
    />
  );
};

export default ContractProductInfo;
